'use client';

import { measure } from '@/components/Analytics/Analytics';
import axios from '@/lib/axios';
import { useEffect, useState } from 'react';
import useSWR from 'swr';

interface BlockUserData {
    submitted_data: number | boolean;
    count: number;
    summary: Array<{
        data: number | boolean;
        count: number;
    }>;
    is_optimistic?: boolean;
}

interface OptimisticBlockUserData extends BlockUserData {
    is_optimistic: true;
}

export const useBlockRemoteUserData = (blockId?: number) => {
    const [
        syncedUserDataRemote,
        setSyncedUserDataRemote,
    ] = useState<BlockUserData | null>(null);

    // Optimistic data is used to show the user a faster response to their interactions
    const [
        optimisticUserDataRemote,
        setOptimisticUserDataRemote,
    ] = useState<OptimisticBlockUserData | null>(null);

    // Fetch remote data
    const {
        data: liveUserDataRemote,
        mutate: revalidateUserData,
    } = useSWR<BlockUserData | null>(
        `block/${blockId ?? 0}/user-data`,
        () =>
            axios
                .post(`block/${blockId ?? 0}/user-data`)
                .then((res) => {
                    setOptimisticUserDataRemote(null);
                    return res.data;
                })
                .catch((error) => {
                    setOptimisticUserDataRemote(null);
                    throw error;
                }),
        {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            errorRetryCount: 3,
            shouldRetryOnError: true,
            errorRetryInterval: 3000,
        },
    );

    useEffect(() => {
        if (liveUserDataRemote) {
            setSyncedUserDataRemote(liveUserDataRemote);
        }
    }, [liveUserDataRemote]);

    // Use optimistic data if available, otherwise use synced or remote data if available
    const userDataRemote =
        optimisticUserDataRemote ?? syncedUserDataRemote ?? liveUserDataRemote;

    // Submit user data to server and provide optimistic data if wanted
    const submitUserDataRemote = (
        value: number | boolean,
        options?: {
            optimisticData?: BlockUserData;
        },
    ) => {
        // Set optimistic data if provided
        if (options?.optimisticData) {
            setOptimisticUserDataRemote({
                ...options?.optimisticData,
                is_optimistic: true,
            });
        }

        measure(`block_user_data_${blockId ?? 0}_${value}`, 'action');

        // Submit data to server
        return axios
            .post(`block/${blockId ?? 0}/user-data`, {
                data: value,
            })
            .then((res) => {
                setOptimisticUserDataRemote(null);
                setSyncedUserDataRemote(res.data);
                return res.data;
            })
            .catch((error) => {
                revalidateUserData();
                setOptimisticUserDataRemote(null);
                setSyncedUserDataRemote(null);
                throw error;
            });
    };

    return {
        userDataRemote,
        submitUserDataRemote,
    };
};
